import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';

export interface UserGamification {
  id: string;
  user_id: string;
  total_points: number;
  diary_streak: number;
  longest_diary_streak: number;
  last_diary_date: string | null;
  tasks_completed: number;
  skills_completed: number;
  created_at: string | null;
  updated_at: string | null;
}

const LEVEL_THRESHOLDS = [0, 100, 300, 600, 1000, 1500, 2500, 4000, 6000, 10000];
const LEVEL_NAMES = ['Beginner', 'Learner', 'Explorer', 'Achiever', 'Performer', 'Expert', 'Master', 'Elite', 'Legend', 'Champion'];

export function getLevel(points: number): number {
  let level = 1;
  for (let i = LEVEL_THRESHOLDS.length - 1; i >= 0; i--) {
    if (points >= LEVEL_THRESHOLDS[i]) { level = i + 1; break; }
  }
  return Math.min(level, 10);
}

export function getLevelName(level: number): string {
  return LEVEL_NAMES[level - 1] || 'Champion';
}

export function getNextLevelThreshold(points: number): number {
  const level = getLevel(points);
  return LEVEL_THRESHOLDS[level] ?? Infinity;
}

export function getLevelProgress(points: number): number {
  const level = getLevel(points);
  const current = LEVEL_THRESHOLDS[level - 1] ?? 0;
  const next = LEVEL_THRESHOLDS[level] ?? points;
  if (next === Infinity) return 100;
  return Math.round(((points - current) / (next - current)) * 100);
}

export function useGamification() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['gamification', user?.id],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await (supabase as any)
        .from('user_gamification')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();
      if (error) throw error;
      return (data as unknown as UserGamification) || null;
    },
    enabled: !!user,
    staleTime: 15_000,
  });
}
